/**
 * Página Admin — Atendimentos ao Cliente (CARD MENOR EMBEDDED)
 *
 * - Renderizada dentro da rota /admin/atendimentos mas com layout de card
 *   centralizado (não ocupa mais a página inteira).
 * - Suporta envio/recebimento de imagens e PDFs (10MB max) via R2.
 * - Pré-visualização inline pra imagens, ícone clicável pra PDFs.
 * - Polling 5s — sem websockets.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiService } from '../../services/api';
import {
  MessageCircle, Send, Phone, X, CheckCircle2, Loader2, RefreshCw,
  Paperclip, FileText, ImageIcon, Download, ArrowLeft,
} from 'lucide-react';

const POLL_MS = 5000;
const MAX_FILE_MB = 10;

const ICONE_CAT = {
  problema_veiculo: '🛠️',
  moto_bloqueada: '🔒',
  pagamento_recusado: '❌',
  debito_em_atraso: '⏰',
  outro: '💬',
};

const fmtData = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const hoje = new Date();
    const ontem = new Date(); ontem.setDate(ontem.getDate() - 1);
    if (d.toDateString() === hoje.toDateString()) {
      return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    }
    if (d.toDateString() === ontem.toDateString()) return 'Ontem';
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  } catch { return ''; }
};

const fmtHora = (iso) => {
  try { return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }); }
  catch { return ''; }
};

const fmtBytes = (b) => {
  if (!b) return '';
  if (b < 1024) return `${b}B`;
  if (b < 1024*1024) return `${(b/1024).toFixed(0)}KB`;
  return `${(b/1024/1024).toFixed(1)}MB`;
};

const AdminAtendimentosPage = () => {
  const [abertos, setAbertos] = useState([]);
  const [fechados, setFechados] = useState([]);
  const [selecionado, setSelecionado] = useState(null);
  const [mensagens, setMensagens] = useState([]);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [carregandoChat, setCarregandoChat] = useState(false);
  const [novaMsg, setNovaMsg] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState(null);
  const [mostrarFechados, setMostrarFechados] = useState(false);
  const [uploadando, setUploadando] = useState(false);
  const [imagemAmpliada, setImagemAmpliada] = useState(null); // { url, nome }
  const fimRef = useRef(null);
  const fileInputRef = useRef(null);
  const pollListaRef = useRef(null);
  const pollChatRef = useRef(null);

  const carregarLista = useCallback(async () => {
    try {
      const { data } = await apiService.api.get('/admin/atendimentos');
      setAbertos(data.abertos || []);
      setFechados(data.fechados || []);
    } catch (e) {
      setErro(e.response?.data?.detail || e.message);
    } finally {
      setCarregandoLista(false);
    }
  }, []);

  const carregarChat = useCallback(async (id) => {
    if (!id) return;
    setCarregandoChat(true);
    try {
      const { data } = await apiService.api.get(`/admin/atendimentos/${id}`);
      setSelecionado(data.atendimento);
      setMensagens(data.mensagens || []);
    } catch (e) {
      setErro(e.response?.data?.detail || e.message);
    } finally {
      setCarregandoChat(false);
    }
  }, []);

  const pollChat = useCallback(async () => {
    if (!selecionado?.id) return;
    try {
      const { data } = await apiService.api.get(`/admin/atendimentos/${selecionado.id}`);
      setMensagens(data.mensagens || []);
      setSelecionado(data.atendimento);
    } catch {/* silent */}
  }, [selecionado?.id]);

  const enviar = async () => {
    const txt = novaMsg.trim();
    if (!txt || !selecionado?.id || enviando) return;
    setEnviando(true);
    try {
      const { data } = await apiService.api.post(
        `/admin/atendimentos/${selecionado.id}/mensagem`,
        { texto: txt }
      );
      setMensagens(prev => [...prev, data.mensagem]);
      setNovaMsg('');
      carregarLista();
    } catch (e) {
      setErro(e.response?.data?.detail || e.message);
    } finally {
      setEnviando(false);
    }
  };

  const enviarArquivo = async (file) => {
    if (!file || !selecionado?.id || uploadando) return;
    // validação de tamanho local (UX)
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      setErro(`Arquivo muito grande (${(file.size/1024/1024).toFixed(1)}MB). Máximo ${MAX_FILE_MB}MB.`);
      return;
    }
    setErro(null);
    setUploadando(true);
    const fd = new FormData();
    fd.append('arquivo', file);
    try {
      const { data } = await apiService.api.post(
        `/admin/atendimentos/${selecionado.id}/mensagem/arquivo`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setMensagens(prev => [...prev, data.mensagem]);
      carregarLista();
    } catch (e) {
      setErro(e.response?.data?.detail || 'Erro ao enviar arquivo');
    } finally {
      setUploadando(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const fechar = async () => {
    if (!selecionado?.id) return;
    if (!window.confirm(`Encerrar atendimento de ${selecionado.cliente_nome}?`)) return;
    try {
      await apiService.api.post(`/admin/atendimentos/${selecionado.id}/fechar`);
      await carregarLista();
      setSelecionado(null);
      setMensagens([]);
    } catch (e) {
      setErro(e.response?.data?.detail || e.message);
    }
  };

  useEffect(() => {
    carregarLista();
    pollListaRef.current = setInterval(carregarLista, POLL_MS);
    return () => clearInterval(pollListaRef.current);
  }, [carregarLista]);

  useEffect(() => {
    if (!selecionado?.id) return;
    pollChatRef.current = setInterval(pollChat, POLL_MS);
    return () => clearInterval(pollChatRef.current);
  }, [selecionado?.id, pollChat]);

  useEffect(() => {
    fimRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [mensagens]);

  const lista = mostrarFechados ? fechados : abertos;

  return (
    <div
      className="-mx-4 -my-4 lg:m-0 lg:px-6 lg:py-4"
      data-testid="admin-atendimentos-page"
    >
      {/* CSS dedicado pro card — escala bem em mobile e desktop */}
      <style>{`
        .mobile-chat-card {
          /* Mobile: ocupa o espaço entre o header admin (~175px) e o fim da viewport,
             descontando padding-bottom do <main> (pb-20 = 80px) + um respiro. */
          height: calc(100dvh - 270px);
          min-height: 420px;
          max-height: 760px;
        }
        @media (min-width: 1024px) {
          .mobile-chat-card {
            /* Desktop: usa 72vh limitado a 700px, centralizado horizontal. */
            height: min(72vh, 700px);
            min-height: 500px;
            margin-left: auto;
            margin-right: auto;
          }
        }
      `}</style>
      {/* CARD — full-screen em mobile, max-w-6xl no desktop */}
      <div
        className="w-full max-w-6xl lg:mx-auto bg-slate-900/60 lg:border lg:border-slate-700/60 lg:rounded-2xl lg:shadow-2xl overflow-hidden flex flex-col lg:flex-row mobile-chat-card"
      >
        {/* SIDEBAR — escondida em mobile quando há atendimento selecionado */}
        <div
          className={`w-full lg:w-72 lg:border-r border-slate-700/60 flex flex-col bg-slate-900/40 ${selecionado ? 'hidden lg:flex' : 'flex'}`}
        >
          <div className="p-3 border-b border-slate-700/60 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-white flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-emerald-400" />
              Atendimentos
            </h2>
            <button
              onClick={() => { setCarregandoLista(true); carregarLista(); }}
              className="text-slate-400 hover:text-white text-xs"
              data-testid="atendimentos-recarregar"
              title="Atualizar"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="flex border-b border-slate-700/60 text-xs">
            <button
              onClick={() => setMostrarFechados(false)}
              className={`flex-1 py-2 ${!mostrarFechados ? 'text-emerald-400 border-b-2 border-emerald-400 font-semibold' : 'text-slate-400'}`}
              data-testid="tab-abertos"
            >
              Abertos ({abertos.length})
            </button>
            <button
              onClick={() => setMostrarFechados(true)}
              className={`flex-1 py-2 ${mostrarFechados ? 'text-slate-300 border-b-2 border-slate-300 font-semibold' : 'text-slate-400'}`}
              data-testid="tab-fechados"
            >
              Fechados ({fechados.length})
            </button>
          </div>

          <div className="flex-1 overflow-y-auto" data-testid="atendimentos-lista">
            {carregandoLista ? (
              <div className="p-6 text-center text-slate-400 text-sm">
                <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                Carregando...
              </div>
            ) : lista.length === 0 ? (
              <div className="p-6 text-center text-slate-400 text-sm">
                {mostrarFechados ? 'Nenhum atendimento encerrado.' : 'Nenhum atendimento em aberto.'}
              </div>
            ) : (
              lista.map(at => (
                <button
                  key={at.id}
                  onClick={() => carregarChat(at.id)}
                  className={`w-full text-left p-3 border-b border-slate-700/30 hover:bg-slate-700/30 transition flex items-start gap-2 ${selecionado?.id === at.id ? 'bg-slate-700/40' : ''}`}
                  data-testid={`atendimento-item-${at.id.slice(0,8)}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-white truncate">{at.cliente_nome || '?'}</p>
                      <span className="text-xs text-slate-500 whitespace-nowrap">{fmtData(at.ultima_msg_em)}</span>
                    </div>
                    {at.categoria_label && (
                      <p className="text-[10px] uppercase tracking-wider text-emerald-300 mt-0.5">
                        {ICONE_CAT[at.categoria] || '💬'} {at.categoria_label}
                      </p>
                    )}
                    <p className="text-xs text-slate-400 truncate mt-0.5">{at.ultima_msg_preview || '...'}</p>
                    {at.mensagens_nao_lidas_admin > 0 && (
                      <span className="inline-block mt-1 px-2 py-0.5 bg-emerald-500 text-slate-900 text-xs rounded-full font-bold" data-testid={`badge-naolido-${at.id.slice(0,8)}`}>
                        {at.mensagens_nao_lidas_admin}
                      </span>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* CHAT — escondido em mobile quando nenhum atendimento selecionado */}
        <div className={`flex-1 flex flex-col min-w-0 min-h-0 ${!selecionado ? 'hidden lg:flex' : 'flex'}`}>
          {!selecionado ? (
            <div className="flex-1 flex items-center justify-center text-slate-400 text-sm" data-testid="atendimento-estado-vazio">
              <div className="text-center">
                <MessageCircle className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                Selecione um atendimento à esquerda pra conversar
              </div>
            </div>
          ) : (
            <>
              {/* HEADER do chat — com botão voltar em mobile */}
              <div className="p-3 border-b border-slate-700/60 flex items-center gap-2 bg-slate-900/50">
                {/* Botão voltar (só mobile) */}
                <button
                  onClick={() => setSelecionado(null)}
                  className="lg:hidden shrink-0 p-1.5 -ml-1 text-slate-300 hover:text-white rounded-lg hover:bg-slate-800 transition"
                  data-testid="btn-voltar-lista-atendimentos"
                  aria-label="Voltar para lista"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm font-semibold text-white truncate">{selecionado.cliente_nome}</h3>
                  {selecionado.categoria_label && (
                    <span className="inline-block mt-0.5 mr-2 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide bg-emerald-500/20 border border-emerald-500/40 text-emerald-300">
                      {ICONE_CAT[selecionado.categoria] || '💬'} {selecionado.categoria_label}
                    </span>
                  )}
                  <p className="text-xs text-slate-400 flex items-center gap-2 mt-0.5 flex-wrap">
                    <span className={`inline-block w-2 h-2 rounded-full ${selecionado.status === 'fechado' ? 'bg-slate-500' : 'bg-emerald-400'}`}></span>
                    {selecionado.status}
                    {selecionado.cliente_telefone && (
                      <a
                        href={`https://wa.me/55${selecionado.cliente_telefone.replace(/\D/g, '')}`}
                        target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1 text-emerald-400 hover:underline"
                      >
                        <Phone className="w-3 h-3" /> WhatsApp
                      </a>
                    )}
                  </p>
                </div>
                {selecionado.status !== 'fechado' && (
                  <button
                    onClick={fechar}
                    className="text-xs text-red-300 hover:text-red-200 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 rounded-lg px-2 sm:px-3 py-1.5 flex items-center gap-1 sm:gap-1.5 transition shrink-0"
                    data-testid="btn-fechar-atendimento"
                  >
                    <X className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Encerrar</span>
                  </button>
                )}
              </div>

              {/* MENSAGENS */}
              <div
                className="flex-1 min-h-0 overflow-y-auto p-3 sm:p-4 space-y-3 bg-slate-900/20"
                data-testid="chat-mensagens"
                style={{ overscrollBehavior: 'contain' }}
              >
                {carregandoChat ? (
                  <div className="text-center text-slate-400 text-sm">Carregando...</div>
                ) : (
                  mensagens.map(msg => (
                    <MsgBubbleAdmin
                      key={msg.id}
                      msg={msg}
                      onAbrirImagem={(url, nome) => setImagemAmpliada({ url, nome })}
                    />
                  ))
                )}
                <div ref={fimRef} />
              </div>

              {/* INPUT */}
              {selecionado.status !== 'fechado' && (
                <div
                  className="p-2 sm:p-3 border-t border-slate-700/60 flex items-end gap-2 bg-slate-900/40"
                  style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
                    className="hidden"
                    onChange={(e) => enviarArquivo(e.target.files?.[0])}
                    data-testid="admin-chat-arquivo-input"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadando || enviando}
                    className="shrink-0 text-slate-400 hover:text-emerald-400 disabled:opacity-40 rounded-lg p-2 sm:p-2.5 transition"
                    title="Enviar foto ou PDF (10MB max)"
                    data-testid="admin-chat-anexo-btn"
                    aria-label="Anexar arquivo"
                  >
                    {uploadando ? <Loader2 className="w-5 h-5 animate-spin" /> : <Paperclip className="w-5 h-5" />}
                  </button>
                  <textarea
                    value={novaMsg}
                    onChange={(e) => setNovaMsg(e.target.value)}
                    placeholder="Escreva sua resposta..."
                    rows={1}
                    maxLength={2000}
                    className="flex-1 min-w-0 bg-slate-800 border border-slate-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500 resize-none"
                    style={{ maxHeight: '120px' }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); }
                    }}
                    data-testid="admin-chat-input"
                  />
                  <button
                    onClick={enviar}
                    disabled={enviando || !novaMsg.trim()}
                    className="shrink-0 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-slate-900 rounded-lg p-2.5 transition min-w-[44px] flex items-center justify-center"
                    data-testid="admin-chat-enviar"
                    aria-label="Enviar mensagem"
                  >
                    {enviando ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                  </button>
                </div>
              )}
              {selecionado.status === 'fechado' && (
                <div className="p-3 border-t border-slate-700/60 text-center text-xs text-slate-500 bg-slate-900/40 flex items-center justify-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Atendimento encerrado
                </div>
              )}
            </>
          )}
        </div>

        {erro && (
          <div className="fixed bottom-4 right-4 bg-red-900/90 border border-red-500/50 text-red-200 rounded-lg px-4 py-2 text-sm max-w-md z-50" data-testid="atendimento-admin-erro">
            <div className="flex items-start gap-2">
              <span className="flex-1">{erro}</span>
              <button onClick={() => setErro(null)} className="text-red-300 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
          </div>
        )}
      </div>

      {/* LIGHTBOX de imagem ampliada */}
      {imagemAmpliada && (
        <div
          className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4"
          onClick={() => setImagemAmpliada(null)}
          data-testid="lightbox-imagem"
        >
          <button
            onClick={() => setImagemAmpliada(null)}
            className="absolute top-4 right-4 text-white bg-slate-800/80 hover:bg-slate-700 rounded-full p-2"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
          <img
            src={imagemAmpliada.url}
            alt={imagemAmpliada.nome}
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

const MsgBubbleAdmin = ({ msg, onAbrirImagem }) => {
  const isAdmin = msg.autor === 'admin';
  const isSistema = msg.autor === 'sistema';
  const temArquivo = !!msg.arquivo_key;

  if (isSistema) {
    return (
      <div className="flex justify-center">
        <div className="rounded-full px-3 py-1 text-xs bg-slate-800 text-slate-400">{msg.texto}</div>
      </div>
    );
  }

  return (
    <div className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] sm:max-w-[75%] rounded-2xl px-3 py-2 sm:px-4 ${
          isAdmin ? 'bg-emerald-500 text-slate-900' : 'bg-slate-700 text-white'
        }`}
      >
        {!isAdmin && msg.autor_nome && (
          <p className="text-[10px] uppercase tracking-wider mb-0.5 opacity-70">{msg.autor_nome}</p>
        )}
        {temArquivo && <AnexoPreview msg={msg} isAdmin={isAdmin} onAbrirImagem={onAbrirImagem} />}
        {msg.texto && (
          <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.texto}</p>
        )}
        <p className={`text-[10px] mt-1 ${isAdmin ? 'text-slate-800' : 'text-slate-400'} text-right`}>
          {fmtHora(msg.criado_em)}
        </p>
      </div>
    </div>
  );
};

/**
 * Renderiza anexo (imagem ou arquivo) carregando a URL pré-assinada sob demanda.
 */
const AnexoPreview = ({ msg, isAdmin, onAbrirImagem }) => {
  const [url, setUrl] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    let alivo = true;
    apiService.api.get(`/atendimento/mensagens/${msg.id}/arquivo`)
      .then(({ data }) => { if (alivo) setUrl(data.url); })
      .catch(() => { if (alivo) setErro(true); })
      .finally(() => { if (alivo) setCarregando(false); });
    return () => { alivo = false; };
  }, [msg.id]);

  if (carregando) {
    return (
      <div className={`flex items-center gap-2 py-2 text-xs ${isAdmin ? 'text-slate-700' : 'text-slate-300'}`}>
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Carregando anexo...
      </div>
    );
  }

  if (erro || !url) {
    return (
      <div className={`text-xs italic ${isAdmin ? 'text-red-900' : 'text-red-300'}`}>
        Falha ao carregar anexo
      </div>
    );
  }

  if (msg.arquivo_eh_imagem) {
    return (
      <button
        onClick={() => onAbrirImagem?.(url, msg.arquivo_nome)}
        className="block mb-1 rounded-lg overflow-hidden hover:opacity-90 transition"
        data-testid={`anexo-imagem-${msg.id.slice(0,8)}`}
      >
        <img
          src={url}
          alt={msg.arquivo_nome}
          className="max-w-[260px] max-h-[260px] object-cover rounded-lg"
          loading="lazy"
        />
      </button>
    );
  }

  // PDF (ou outro)
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className={`flex items-center gap-2 rounded-lg px-2 py-2 mb-1 ${
        isAdmin
          ? 'bg-slate-900/20 hover:bg-slate-900/30 text-slate-800'
          : 'bg-slate-600/50 hover:bg-slate-600 text-white'
      } transition`}
      data-testid={`anexo-arquivo-${msg.id.slice(0,8)}`}
    >
      <FileText className="w-6 h-6 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold truncate">{msg.arquivo_nome || 'arquivo.pdf'}</p>
        <p className="text-[10px] opacity-70">{fmtBytes(msg.arquivo_tamanho)} • Clique pra abrir</p>
      </div>
      <Download className="w-4 h-4 shrink-0 opacity-70" />
    </a>
  );
};

export default AdminAtendimentosPage;
